import { getReasonOrEntityLabels } from '@modules/common/utils';
import {
  generateKeywordSummaryUnitsMap,
  getReasonSummaryUnits,
} from '@modules/feedback/components/AudioType/utils';
import { KeywordOrReasonSummaryList } from '@modules/feedback/components/KeywordsAndReasonsSummary/KeywordOrReasonSummaryList';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { getDeletedEditPredictionLabelIDs } from '@modules/feedback/utils';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import { EastRounded } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { SummaryMetadata } from 'generated/graphql';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';

interface KeywordsAndReasonsSummaryProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[] | undefined;
  onClick: (metadata: SummaryMetadata | undefined) => void;
  shouldAnimate?: (metadata: SummaryMetadata | undefined) => boolean;
  setSelectedLabels?: (labels: FeedbackLabel[]) => void;
}

export const KeywordsAndReasonsSummary = ({
  feedback,
  selectedLabels,
  onClick,
  shouldAnimate,
  setSelectedLabels,
}: KeywordsAndReasonsSummaryProps) => {
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const editedRecord = editedRecords.find(
    (editedRecord) => editedRecord.id === feedback.id,
  );

  const deletedEditPredictionLabelIDs =
    getDeletedEditPredictionLabelIDs(editedRecord);

  const selectedKeywordsOrReasons = getReasonOrEntityLabels(
    selectedLabels ?? [],
  );

  // Handles the case wherein a selected label is removed by the user
  const filteredSelectedKeywordsOrReasons = selectedKeywordsOrReasons.filter(
    (label) => !deletedEditPredictionLabelIDs.includes(label.id),
  );

  if (filteredSelectedKeywordsOrReasons.length === 0) {
    return (
      <Stack
        height="100%"
        direction="row"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        spacing={2}
      >
        <Stack>
          <Typography
            variant={isFeedbackRedesignEnabled ? 'subtitle1' : 'h6'}
            color="text.secondary"
          >
            Select Tracked Keyword(s) or
          </Typography>
          <Typography
            variant={isFeedbackRedesignEnabled ? 'subtitle1' : 'h6'}
            color="text.secondary"
          >
            Reason(s) to see their summaries
          </Typography>
        </Stack>
        <EastRounded
          sx={{
            color: 'text.secondary',
          }}
        />
      </Stack>
    );
  }

  const keywordSummaryUnitsMap = generateKeywordSummaryUnitsMap(
    feedback.labels ?? [],
  );

  const audioConversation =
    feedback.content.type === FeedbackContentType.Audio
      ? feedback.content.audioConversationEn
      : undefined;

  return (
    <Box pb={5}>
      {filteredSelectedKeywordsOrReasons.map((label, index) => (
        <KeywordOrReasonSummaryList
          index={index}
          key={label.id}
          label={label}
          summaryUnits={
            label.type === FeedbackLabelType.Entity
              ? label?.keywordSummary?.units
              : getReasonSummaryUnits(
                  label,
                  keywordSummaryUnitsMap,
                  audioConversation,
                )
          }
          onClick={onClick}
          shouldAnimate={shouldAnimate}
          selectedLabels={selectedLabels}
          feedback={feedback}
          setSelectedLabels={setSelectedLabels}
        />
      ))}
    </Box>
  );
};
